import { Component, OnInit, Input, ChangeDetectorRef, OnDestroy, EventEmitter, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { Permissions } from '@bciti/types';
import { allNavigationItemsSelector, availableLanguagesSelector, organisationConfigurationSelector } from 'src/app/global/store/global.selectors';
import { FormBuilder, FormControl, Validators, FormGroup, AbstractControl, FormArray } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil, tap, startWith } from 'rxjs/operators';
import { combineLatest, Subject } from 'rxjs';
import { CreateAccessAction, CreateAccessSuccessAction, CreateAccessErrorAction, UpdateAccessAction, UpdateAccessSuccessAction, UpdateAccessErrorAction } from 'src/app/global/store/global.actions';
import { Actions, ofType } from '@ngrx/effects';
import * as Moment from 'moment';
import { extendMoment } from 'moment-range';
import { AccessControl, CreateRoleDto, UpdateRoleDto } from 'src/app/global/services/access-control/access-control.model';
import { NavigationItem, OrganisationConfiguration } from 'src/app/global/global.model';
export const moment = extendMoment(Moment);

interface ModuleWithLabels {
    label: string;
    value: string;
}

const moduleLabelsDictionary: { [key: string]: { en: string; fr: string } } = {
    Requests: { en: 'Requests', fr: 'Requêtes' },
    Cards: { en: 'Cards', fr: 'Cartes' },
    Subscriptions: { en: 'Subscriptions', fr: 'Abonnements' },
    Receipts: { en: 'Receipts', fr: 'Factures' },
    Family: { en: 'Family', fr: 'Famille' },
    History: { en: 'History', fr: 'Historique' },
};

@Component({
    selector: 'app-access-control-form',
    templateUrl: './access-control-form.component.html',
    styleUrls: ['./access-control-form.component.scss'],
})
export class AccessControlFormComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();

    @Input() access: AccessControl;
    @Input() title: string;
    @Output() submitForm = new EventEmitter<AccessControl>();
    @Input() submitLoading = false;
    @Input() isEditMode = false;

    public accessForm = this.fb.group({
        title: this.fb.group({}),
        description: this.fb.group({}),
    });

    loading = false;
    public modules: ModuleWithLabels[] = [];
    public activeModules: ModuleWithLabels[] = [];

    get nameForm(): AbstractControl {
        return this.accessForm.get('title');
    }

    get descriptionForm(): AbstractControl {
        return this.accessForm.get('description');
    }

    langs: string[];

    get currentLang(): string {
        return this.translate.currentLang;
    }

    constructor(
        private router: Router,
        private store: Store<State>,
        private actions$: Actions,
        private fb: FormBuilder,
        private cdr: ChangeDetectorRef,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        // Use combineLatest to wait for both observables to emit
        combineLatest([this.store.select(allNavigationItemsSelector), this.store.select(organisationConfigurationSelector), this.store.select(availableLanguagesSelector)])
            .pipe(
                takeUntil(this.destroy$),
                tap(([mainNavItems, orgConfig, langs]) => {
                    this.modules = this.getAvailableModulesForOrganisation(mainNavItems, orgConfig);
                    this.langs = [this.currentLang, ...[...langs].filter((l) => this.currentLang !== l)];

                    this.accessForm = this.createAccessForm();

                    if (this.langs.length === 1) {
                        const lang = this.langs[0];
                        (this.accessForm.get('title') as FormGroup).addControl(lang, new FormControl('', [Validators.required]));
                        (this.accessForm.get('description') as FormGroup).addControl(lang, new FormControl('', [Validators.required]));
                    } else {
                        this.langs.forEach((lang) => {
                            const nameControl = new FormControl('', [Validators.required]);
                            (this.accessForm.get('title') as FormGroup).addControl(lang, nameControl);
                            const descriptionControl = new FormControl('', [Validators.required]);
                            (this.accessForm.get('description') as FormGroup).addControl(lang, descriptionControl);
                        });
                    }

                    // Now configure the form if in edit mode - form is fully initialized
                    if (this.isEditMode && this.access) {
                        this.configureForm(this.access);
                    }
                }),
            )
            .subscribe();

        this.translate.onLangChange
            .pipe(
                startWith(''),
                takeUntil(this.destroy$),
                tap(() => {}),
            )
            .subscribe();
    }

    public checkActiveModules(): boolean {
        const newActiveModules: ModuleWithLabels[] = [];

        this.modules.forEach((module) => {
            const permissionGroup = this.accessForm.get(module.value);
            if (permissionGroup && permissionGroup.get('active')?.value) {
                newActiveModules.push(module);
            }
        });

        this.activeModules = newActiveModules;

        return this.activeModules.length > 0;
    }

    private configureForm(access: AccessControl): void {
        const titleControl = this.accessForm.get('title');
        this.langs.forEach((lang) => titleControl.get(lang).setValue(access.title[lang] || ''));

        const descriptionControl = this.accessForm.get('description');
        this.langs.forEach((lang) => descriptionControl.get(lang).setValue(access.description[lang] || ''));

        access.permissions.forEach((module) => {
            const moduleControl = this.accessForm.get(module);
            if (moduleControl) {
                const activeControl = moduleControl.get('active');
                if (activeControl) {
                    activeControl.setValue(true);
                }
            }
        });

        // Populate scopes if they exist
        if (access.scopes && access.scopes.length > 0) {
            access.scopes.forEach((scope) => {
                const moduleControl = this.accessForm.get(scope.permission);
                if (moduleControl) {
                    const scopesArray = moduleControl.get('scopes') as FormArray;
                    const scopeGroup = this.fb.group({
                        permission: [scope.permission, Validators.required],
                        resource: [scope.resource, Validators.required],
                        resourceId: [scope.resourceId, Validators.required],
                        accessLevel: [scope.accessLevel, Validators.required],
                    });
                    scopesArray.push(scopeGroup);
                }
            });
        }
    }

    private createAccessForm(): FormGroup {
        const formGroups: { [key: string]: FormGroup } = {
            title: this.fb.group({}),
            description: this.fb.group({}),
        };

        this.modules.forEach((module) => {
            formGroups[module.value] = this.fb.group({
                active: new FormControl(false),
                read: new FormControl(false),
                edit: new FormControl(false),
                delete: new FormControl(false),
                fullAccess: new FormControl(false),
                scopes: this.fb.array([]),
            });
        });

        return this.fb.group(formGroups);
    }

    saveRole(): void {
        this.accessForm.updateValueAndValidity();
        if (this.isEditMode) {
            const payload: UpdateRoleDto = this.createUpdatePayload();
            this.store.dispatch(
                UpdateAccessAction({
                    access: payload,
                    roleId: this.access._id,
                }),
            );
            this.actions$
                .pipe(
                    ofType(UpdateAccessSuccessAction, UpdateAccessErrorAction),
                    tap(() => (this.submitLoading = false)),
                    ofType(UpdateAccessSuccessAction),
                    tap(() => this.router.navigate(['access-control'])),
                )
                .subscribe();
        } else {
            const payload: CreateRoleDto = this.createPayload();
            this.store.dispatch(
                CreateAccessAction({
                    access: payload,
                }),
            );
            this.actions$
                .pipe(
                    ofType(CreateAccessSuccessAction, CreateAccessErrorAction),
                    tap(() => (this.submitLoading = false)),
                    ofType(CreateAccessSuccessAction),
                    tap(() => this.router.navigate(['access-control'])),
                )
                .subscribe();
        }
    }

    private createPayload(): CreateRoleDto {
        return this.buildRolePayload();
    }

    private createUpdatePayload(): UpdateRoleDto {
        return this.buildRolePayload();
    }

    private buildRolePayload(): CreateRoleDto {
        return {
            key: 'role',
            title: {
                fr: this?.accessForm?.get('title.fr')?.value || this?.accessForm?.get('title.en')?.value,
                en: this?.accessForm?.get('title.en')?.value || this?.accessForm?.get('title.fr')?.value,
            },
            description: {
                fr: this?.accessForm?.get('description.fr')?.value || this?.accessForm?.get('description.en')?.value,
                en: this?.accessForm?.get('description.en')?.value || this?.accessForm?.get('description.fr')?.value,
            },
            permissions: this.getActivePermissions(),
            type: this?.accessForm?.get('title').get(this.currentLang)?.value || '',
            scopes: this.getAllScopes(),
        };
    }

    private getActivePermissions(): string[] {
        const activePermissions: string[] = [];
        this.modules.forEach((module) => {
            const permissionGroup = this.accessForm.get(module.value);
            const activeValue = permissionGroup?.get('active')?.value;
            if (permissionGroup && activeValue) {
                activePermissions.push(module.value);
            }
        });

        if (activePermissions.length === 0) {
            this.modules.forEach((module) => {
                activePermissions.push(module.value);
            });
        }

        return activePermissions;
    }

    private getAllScopes(): any[] {
        const allScopes: any[] = [];

        this.modules.forEach((module) => {
            const permissionGroup = this.accessForm.get(module.value);
            if (permissionGroup && permissionGroup.get('active')?.value) {
                const scopesArray = permissionGroup.get('scopes') as FormArray;
                if (scopesArray && scopesArray.length > 0) {
                    scopesArray.controls.forEach((scopeControl) => {
                        const scopeValue = scopeControl.value;
                        if (scopeValue.resource && scopeValue.resourceId) {
                            allScopes.push({
                                permission: scopeValue.permission,
                                resource: scopeValue.resource,
                                resourceId: scopeValue.resourceId,
                                accessLevel: scopeValue.accessLevel,
                            });
                        }
                    });
                }
            }
        });

        return allScopes;
    }

    private getAvailableModulesForOrganisation(mainNavItems: any[], orgConfig: OrganisationConfiguration): ModuleWithLabels[] {
        const allPermissionModules = Object.values(Permissions) as string[];
        const availableModules: ModuleWithLabels[] = [];

        mainNavItems?.forEach((item: NavigationItem) => {
            if (item?.module) {
                if (allPermissionModules.includes(item?.module) && !availableModules.some((m) => m.value === item?.module)) {
                    availableModules.push({ label: item.text, value: item.module });
                }
            }
        });

        orgConfig.adminTabs?.forEach((tab) => {
            if (tab?.tag && allPermissionModules.includes(tab?.tag) && !availableModules.some((m) => m.value === tab?.tag)) {
                availableModules.push({ label: moduleLabelsDictionary[tab.name]?.[this.currentLang], value: tab.tag });
            }
        });

        return availableModules;
    }

    public goBack() {
        this.router.navigate(['/access-control']);
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
